import React from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';

// TODO: Add ICO registration number once registered (see docs/tasks/todo/manual/task-register-with-ico.md)
const ICO_REGISTRATION_NUMBER: string | null = null;

export default function PrivacyPage() {
  return (
    <div className="landing">
      <PublicHeader />

      <main className="privacy-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: March 2026</p>

        <h2>Who we are</h2>
        <p>
          The Email Helper is operated by <strong>Oblivion Solutions Limited</strong>, a company
          registered in England and Wales. Our registered office address is 124 City Road, London,
          United Kingdom, EC1V 2NX.
        </p>
        <p>
          Oblivion Solutions Limited is the data controller for the personal data processed through
          this service.
          {ICO_REGISTRATION_NUMBER && (
            <> Our ICO registration number is <strong>{ICO_REGISTRATION_NUMBER}</strong>.</>
          )}
        </p>
        <p>
          If you have any questions about this policy or how we handle your data, contact us at{' '}
          <a href="mailto:privacy@theemailhelper.com">privacy@theemailhelper.com</a>.
        </p>

        <h2>What data we collect</h2>
        <p>We collect and process the following categories of personal data:</p>
        <ul>
          <li>
            <strong>Account identity:</strong> Your name and email address, as provided by Google
            or Microsoft when you sign in.
          </li>
          <li>
            <strong>Email metadata:</strong> Sender address, recipient address, subject line, date,
            and email headers (including <code>List-Unsubscribe</code> headers used to identify
            subscription emails). We do not store the full body of your emails for categorisation
            purposes, though email content may be processed temporarily when generating
            auto-response drafts (see AI Processing below).
          </li>
          <li>
            <strong>OAuth tokens:</strong> Access and refresh tokens issued by Google or Microsoft,
            used to read and manage your emails on your behalf. These are stored securely and never
            shared.
          </li>
          <li>
            <strong>Usage and action data:</strong> A record of actions you take within the app
            (e.g. archiving a sender, applying a label), used to support undo functionality and
            your action history.
          </li>
          <li>
            <strong>Billing data:</strong> Subscription status and payment identifiers. Full
            payment card details are handled directly by our payment processors (Stripe and
            GoCardless) and are never stored by us.
          </li>
          <li>
            <strong>Browser storage:</strong> We use <code>localStorage</code> (not cookies) to
            store UI preferences such as your onboarding progress and sidebar state. This data
            never leaves your device and is not transmitted to our servers.
          </li>
        </ul>

        <h2>How we access your email — Gmail</h2>
        <p>
          When you connect a Gmail account, we access your mailbox via the{' '}
          <strong>Gmail API</strong>, using Google OAuth 2.0 authentication. We request the{' '}
          <code>gmail.modify</code> scope, which allows us to:
        </p>
        <ul>
          <li>Read email metadata (sender, subject, date, headers)</li>
          <li>Archive, label, and move emails to trash on your explicit instruction</li>
          <li>Watch your inbox for new emails in real time via Gmail push notifications</li>
        </ul>
        <p>
          We do <strong>not</strong> read or store full email bodies for categorisation. We do{' '}
          <strong>not</strong> send emails on your behalf unless you explicitly request an
          auto-response draft and choose to send it.
        </p>
        <p>
          <strong>
            We do not sell, transfer, or share your Gmail data with any third party for advertising
            or any other purpose, except as described in the AI Processing and Third Parties
            sections below.
          </strong>{' '}
          Our use of Gmail data is limited to providing and improving the features you use within
          The Email Helper and is not used to serve ads.
        </p>
        <p>
          Our use and transfer of information received from Google APIs adheres to the{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>

        <h2>How we access your email — Microsoft Outlook</h2>
        <p>
          When you connect a Microsoft account (Outlook, Hotmail, or Microsoft 365), we access
          your mailbox via the <strong>Microsoft Graph API</strong>, using Microsoft OAuth 2.0
          authentication. We request scopes that allow us to:
        </p>
        <ul>
          <li>Read email metadata (sender, subject, date, headers)</li>
          <li>Archive, label, and move emails to trash on your explicit instruction</li>
        </ul>
        <p>
          The same restrictions apply as for Gmail: we do not read or store full email bodies for
          categorisation, we do not send emails without your explicit instruction, and we do not
          sell or share your Microsoft email data with any third party for advertising or any other
          purpose, except as described below.
        </p>

        <h2>AI processing</h2>
        <p>
          We use large language models (LLMs) provided by{' '}
          <strong>Anthropic</strong> (Claude) to power two features:
        </p>
        <ul>
          <li>
            <strong>Email categorisation:</strong> When our rules-based classifier cannot
            confidently categorise an email, we send the sender address and subject line to
            Anthropic's API to classify it. Full email bodies are not sent for this purpose.
          </li>
          <li>
            <strong>Auto-response drafting:</strong> When you request a suggested reply to an
            email, we send the relevant email content (including the body of the email you are
            replying to) to Anthropic's API to generate a draft. You review the draft before
            anything is sent.
          </li>
        </ul>
        <p>
          Anthropic processes this data as a data processor acting on our behalf, subject to their{' '}
          <a
            href="https://www.anthropic.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            privacy policy
          </a>
          . Data sent to Anthropic is not used to train their models.
        </p>

        <h2>How we use your data</h2>
        <p>We use your personal data for the following purposes:</p>
        <ul>
          <li>To provide the inbox categorisation, organisation, and management service</li>
          <li>To perform actions in your mailbox (archive, label, trash) on your instruction</li>
          <li>To generate suggested auto-response drafts on your request</li>
          <li>To manage your subscription and process payments</li>
          <li>To respond to support or privacy enquiries</li>
        </ul>
        <p>
          <strong>Lawful basis (UK GDPR):</strong> We process your data on the basis of{' '}
          <em>performance of a contract</em> — providing the service you have signed up for. Where
          we process data beyond what is strictly necessary to deliver the service (e.g. service
          improvement), we rely on <em>legitimate interests</em>.
        </p>

        <h2>Third parties</h2>
        <p>We share data with the following third-party services in order to operate:</p>
        <ul>
          <li>
            <strong>Google</strong> — OAuth authentication and Gmail API access.{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Privacy Policy
            </a>
          </li>
          <li>
            <strong>Microsoft</strong> — OAuth authentication, Microsoft Graph API access, and
            Azure cloud infrastructure (database and application hosting in UK data centres).{' '}
            <a
              href="https://privacy.microsoft.com/en-gb/privacystatement"
              target="_blank"
              rel="noopener noreferrer"
            >
              Microsoft Privacy Statement
            </a>
          </li>
          <li>
            <strong>Anthropic</strong> — LLM processing for email categorisation and auto-response
            drafting (email metadata and, for auto-responses, email content).{' '}
            <a
              href="https://www.anthropic.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Anthropic Privacy Policy
            </a>
          </li>
          <li>
            <strong>Stripe</strong> — Card payment processing. Stripe stores and processes payment
            card details; we store only a Stripe customer identifier.{' '}
            <a
              href="https://stripe.com/gb/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe Privacy Policy
            </a>
          </li>
          <li>
            <strong>GoCardless</strong> — Direct debit payment processing. GoCardless stores and
            processes bank mandate details; we store only a GoCardless mandate identifier.{' '}
            <a
              href="https://gocardless.com/privacy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              GoCardless Privacy Policy
            </a>
          </li>
        </ul>
        <p>We do not share your data with any other third parties.</p>

        <h2>Data storage and security</h2>
        <p>
          Your data is stored in <strong>Microsoft Azure data centres located in the United
          Kingdom</strong>. OAuth tokens are encrypted at rest and in transit. We use
          industry-standard security practices to protect your data from unauthorised access.
        </p>

        <h2>Data retention</h2>
        <p>
          We retain your data for as long as your account is active. If you disconnect your email
          account or delete your account, your personal data (email metadata, tokens, and action
          history) will be <strong>permanently deleted within 30 days</strong>. Billing records
          held by Stripe and GoCardless are subject to their own retention policies.
        </p>

        <h2>Your rights</h2>
        <p>Under UK GDPR, you have the following rights regarding your personal data:</p>
        <ul>
          <li>
            <strong>Right of access (Article 15):</strong> You can request a copy of the personal
            data we hold about you.
          </li>
          <li>
            <strong>Right to rectification (Article 16):</strong> You can ask us to correct
            inaccurate data we hold about you.
          </li>
          <li>
            <strong>Right to erasure (Article 17):</strong> You can ask us to delete your personal
            data. You can also disconnect your account from the Settings page, which triggers
            deletion of your email metadata and tokens within 30 days.
          </li>
          <li>
            <strong>Right to data portability (Article 20):</strong> You can request your data in
            a structured, machine-readable format.
          </li>
          <li>
            <strong>Right to object (Article 21):</strong> You can object to processing based on
            legitimate interests.
          </li>
          <li>
            <strong>Right to lodge a complaint:</strong> If you believe we have mishandled your
            data, you have the right to lodge a complaint with the{' '}
            <a
              href="https://ico.org.uk/make-a-complaint"
              target="_blank"
              rel="noopener noreferrer"
            >
              Information Commissioner's Office (ICO)
            </a>
            .
          </li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:privacy@theemailhelper.com">privacy@theemailhelper.com</a>. We will
          respond within 30 days.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          We may update this policy from time to time. The "Last updated" date at the top of this
          page will reflect any changes. Continued use of the service after an update constitutes
          acceptance of the revised policy.
        </p>

        <h2>Contact</h2>
        <p>
          Oblivion Solutions Limited<br />
          124 City Road<br />
          London, United Kingdom<br />
          EC1V 2NX<br />
          <a href="mailto:privacy@theemailhelper.com">privacy@theemailhelper.com</a>
        </p>
      </main>

      <footer className="landing-footer">
        © {new Date().getFullYear()} Oblivion Solutions Limited ·{' '}
        <Link to="/privacy">Privacy</Link> ·{' '}
        <Link to="/terms">Terms</Link>
      </footer>
    </div>
  );
}
